/**
 * Возвращает первую строку из табличного поля, содержащую документные идентификаторы.
 *
 * Ищет строку в значениях `fields` по структурам:
 * - `value.rows`
 * - `rows`
 * - `value` как массив строк
 *
 * Строка считается подходящей, если в ней есть `_id` или `_typeId`
 * (в сыром виде или в ячейке с оберткой `{ value, type }`).
 */
export function getFirstTableRowFromFields(
  fields: Record<string, unknown>,
): Record<string, unknown> | null {
  const cellVal = (cell: unknown): unknown =>
    cell != null && typeof cell === 'object' && 'value' in (cell as object)
      ? (cell as { value: unknown }).value
      : cell
  for (const v of Object.values(fields)) {
    if (!v || typeof v !== 'object') continue
    const o = v as Record<string, unknown>
    const rows =
      o.rows ??
      (o.value && typeof o.value === 'object' && 'rows' in (o.value as object)
        ? (o.value as { rows: unknown[] }).rows
        : null) ??
      (Array.isArray(o.value) ? o.value : null)
    if (Array.isArray(rows) && rows.length > 0 && rows[0] && typeof rows[0] === 'object') {
      const row = rows[0] as Record<string, unknown>
      if (cellVal(row._id) != null || cellVal(row._typeId) != null) return row
    }
  }
  return null
}

/**
 * Нормализует payload перед вызовом `executeProcedure` для сценария `action=getFileData`.
 * TODO: временный костыль для id=201
 *
 * Что делает:
 * - гарантирует объектную структуру `fields.fields`;
 * - принудительно выставляет `action = "getFileData"` в типизированном формате `{ type, value }`;
 * - при отсутствии `_id` / `_versionId` / `_typeId` пытается извлечь их из первой строки табличных полей
 *   (через `getFirstTableRowFromFields`) и добавить в `fields.fields`.
 *
 * Зачем:
 * backend-процедура `getFileData` ожидает эти ключи в `clientData`; без них часть сценариев печати/загрузки
 * может падать или возвращать пустой результат.
 */
export function ensureGetFileDataPayload(currentData: any): any {
  const out = typeof currentData === 'object' && currentData !== null ? { ...currentData } : {}
  let fields = out.fields ?? {}
  if (typeof fields !== 'object') fields = {}
  let inner = fields.fields ?? fields
  if (typeof inner !== 'object') inner = {}
  inner = { ...inner }

  const setField = (key: string, val: unknown) => {
    inner[key] =
      typeof val === 'object' && val !== null && 'type' in (val as object)
        ? val
        : { type: 'String', value: val == null ? '' : String(val) }
  }
  setField('action', 'getFileData')

  const cellVal = (cell: unknown): unknown =>
    cell != null && typeof cell === 'object' && 'value' in (cell as object)
      ? (cell as { value: unknown }).value
      : cell
  const needIds = ['_id', '_versionId', '_typeId'].some((k) => inner[k] == null)
  if (needIds) {
    const firstRow = getFirstTableRowFromFields(inner)
    if (firstRow) {
      const id = cellVal(firstRow._id) ?? cellVal(firstRow.id)
      const ver = cellVal(firstRow._versionId) ?? cellVal(firstRow.versionId)
      const typeId = cellVal(firstRow._typeId) ?? cellVal(firstRow.typeId)
      if (id != null) setField('_id', id)
      if (ver != null) setField('_versionId', ver)
      if (typeId != null) setField('_typeId', typeId)
    }
  }

  fields = { ...fields, fields: inner }
  out.fields = fields
  return out
}

